import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  InfoOutlined,
  LockOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CorrectiveActionImpactTimeline } from './SafetyReportArtifactDetail.impactTimeline';
import { buildImpactTimelineChartPoints } from './SafetyReportArtifactDetail.impactTimeline';
import type { SafetyReportOutcomeContext } from './safetyReportOutcomeContext';

const TONES = {
  baseline: { fill: 'rgba(251, 146, 60, 0.12)', stroke: '#fb923c', label: '#c2410c' },
  followupTrusted: { fill: 'rgba(34, 197, 94, 0.12)', stroke: '#22c55e', label: '#15803d' },
  followupCaution: { fill: 'rgba(245, 158, 11, 0.12)', stroke: '#f59e0b', label: '#b45309' },
  pivot: { stroke: '#2563eb', label: '#1d4ed8' },
  monitored: '#3b82f6',
  partial: '#f59e0b',
  offline: '#94a3b8',
  unknown: '#e2e8f0',
} as const;

function parseTimestamp(value?: string | null): number | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatHours(value: number | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Not included';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}h`;
}

function formatTrustLabel(value: CorrectiveActionImpactTimeline['readiness']['denominator_trust']): string {
  switch (value) {
    case 'trusted':
      return 'Trusted';
    case 'needs_review':
      return 'Needs review';
    case 'insufficient_data':
      return 'Insufficient data';
    default:
      return 'Not included';
  }
}

function formatDateTime(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not included';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateTimeFromMillis(value?: number | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Not included';
  return formatDateTime(new Date(value).toISOString());
}

function dedupeTicks(values: Array<number | null | undefined>): number[] {
  const unique = new Set<number>();
  values.forEach((value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      unique.add(value);
    }
  });
  return Array.from(unique).sort((a, b) => a - b);
}

function badgeTone(status: CorrectiveActionImpactTimeline['status']): 'success' | 'info' | 'warning' | 'default' {
  if (status === 'interpretable') return 'success';
  if (status === 'directional_only' || status === 'waived') return 'info';
  return 'warning';
}

export function ObservationCoverageBand({ timeline }: { timeline: CorrectiveActionImpactTimeline }) {
  const intervals = timeline.observation_intervals;
  if (!intervals.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Observation coverage intervals are not included in this signed-off package.
      </Typography>
    );
  }

  const startMs = Math.min(...intervals.map((item) => parseTimestamp(item.start) ?? Number.MAX_SAFE_INTEGER));
  const endMs = Math.max(...intervals.map((item) => parseTimestamp(item.end) ?? 0));
  const span = Math.max(endMs - startMs, 1);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Observation coverage
      </Typography>
      <Box
        sx={{
          display: 'flex',
          height: 14,
          borderRadius: 999,
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
        }}
        data-testid="observation-coverage-band"
      >
        {intervals.map((interval, index) => {
          const left = ((parseTimestamp(interval.start) ?? startMs) - startMs) / span;
          const right = ((parseTimestamp(interval.end) ?? endMs) - startMs) / span;
          const width = Math.max((right - left) * 100, 0.5);
          const color = interval.status === 'monitored'
            ? TONES.monitored
            : interval.status === 'partial'
            ? TONES.partial
            : interval.status === 'offline'
            ? TONES.offline
            : TONES.unknown;
          return (
            <Box
              key={`${interval.start}-${interval.end}-${index}`}
              data-status={interval.status}
              sx={{ width: `${width}%`, bgcolor: color }}
            />
          );
        })}
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }} flexWrap="wrap">
        {[
          { label: 'Monitored', color: TONES.monitored },
          { label: 'Partial', color: TONES.partial },
          { label: 'Offline', color: TONES.offline },
          { label: 'Unknown', color: TONES.unknown },
        ].map((item) => (
          <Stack key={item.label} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function ShortWindowMarker({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <Chip
      icon={<WarningAmberOutlined />}
      label="Short monitored window"
      size="small"
      color="warning"
      variant="outlined"
      sx={{ mt: 0.5 }}
      data-testid="short-window-warning"
    />
  );
}

function ReadinessRow({ label, value, chip }: { label: string; value?: string; chip?: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {chip || (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      )}
    </Stack>
  );
}

function readinessChipColor(value: string): 'success' | 'warning' | 'error' | 'default' {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'trusted' || normalized === 'interpretable') return 'success';
  if (normalized === 'needs review' || normalized === 'waived' || normalized === 'directional only') return 'warning';
  if (normalized === 'insufficient data') return 'error';
  return 'default';
}

function MeasurementReadinessPanel({ timeline }: { timeline: CorrectiveActionImpactTimeline }) {
  const bucketCount = timeline.event_buckets.length;
  const requiredBuckets = timeline.thresholds.min_bucket_count_for_trend;
  const trustLabel = formatTrustLabel(timeline.readiness.denominator_trust);
  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        p: 2,
        bgcolor: '#f8fafc',
      }}
      data-testid="measurement-readiness-panel"
    >
      <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
        Comparison status
      </Typography>
      <Stack spacing={0.9}>
        <ReadinessRow label="Baseline monitored" value={formatHours(timeline.baseline_window.monitored_hours)} />
        <ReadinessRow label="Follow-up monitored" value={formatHours(timeline.followup_window.monitored_hours)} />
        <ReadinessRow label="Minimum recommended" value={`${formatHours(timeline.thresholds.min_baseline_monitored_hours)} each`} />
        <ReadinessRow
          label="Observation-time reliability"
          chip={<Chip size="small" label={trustLabel} color={readinessChipColor(trustLabel)} variant="outlined" sx={{ fontWeight: 700 }} />}
        />
        <ReadinessRow label="Trend buckets" value={`${bucketCount} of ${requiredBuckets} required`} />
        <ReadinessRow
          label="Comparison result"
          chip={<Chip size="small" label={timeline.status_label} color={readinessChipColor(timeline.status_label)} variant="outlined" sx={{ fontWeight: 700 }} />}
        />
      </Stack>
    </Box>
  );
}

function ImpactTimelineChart({ timeline }: { timeline: CorrectiveActionImpactTimeline }) {
  const chartPoints = useMemo(() => buildImpactTimelineChartPoints(timeline), [timeline]);
  const useRate = timeline.readiness.denominator_trust === 'trusted';

  const domain = useMemo(() => {
    const scopeStart = parseTimestamp(timeline.report_scope.start);
    const scopeEnd = parseTimestamp(timeline.report_scope.end);
    if (scopeStart !== null && scopeEnd !== null && scopeEnd > scopeStart) {
      return [scopeStart, scopeEnd];
    }
    const timestamps = [
      scopeStart,
      scopeEnd,
      parseTimestamp(timeline.baseline_window.start),
      parseTimestamp(timeline.baseline_window.end),
      parseTimestamp(timeline.followup_window.start),
      parseTimestamp(timeline.followup_window.end),
      parseTimestamp(timeline.corrective_action.pivot_timestamp),
      ...chartPoints.map((point) => point.startMs),
      ...chartPoints.map((point) => point.endMs),
    ].filter((value): value is number => typeof value === 'number');
    if (!timestamps.length) return [0, 1];
    const min = Math.min(...timestamps);
    const max = Math.max(...timestamps);
    const padding = Math.max((max - min) * 0.04, 60_000);
    return [min - padding, max + padding];
  }, [chartPoints, timeline]);
  const axisTicks = useMemo(
    () => dedupeTicks([
      parseTimestamp(timeline.report_scope.start),
      parseTimestamp(timeline.baseline_window.start),
      parseTimestamp(timeline.baseline_window.end),
      parseTimestamp(timeline.corrective_action.pivot_timestamp),
      parseTimestamp(timeline.followup_window.start),
      parseTimestamp(timeline.followup_window.end),
      parseTimestamp(timeline.report_scope.end),
    ]),
    [timeline],
  );

  const chartData = chartPoints.map((point) => ({
    ...point,
    displayValue: useRate
      ? (typeof point.rate_per_monitored_hour === 'number' ? point.rate_per_monitored_hour : 0)
      : point.event_count,
    x: point.midpoint,
  }));

  const baselineStart = parseTimestamp(timeline.baseline_window.start);
  const baselineEnd = parseTimestamp(timeline.baseline_window.end);
  const followupStart = parseTimestamp(timeline.followup_window.start);
  const followupEnd = parseTimestamp(timeline.followup_window.end);
  const pivotMs = parseTimestamp(timeline.corrective_action.pivot_timestamp);
  const followupTone = timeline.followup_tone === 'trusted' ? TONES.followupTrusted : TONES.followupCaution;

  return (
    <Box data-testid="impact-timeline-chart" data-timeline-mode={timeline.timeline_mode}>
      <Box sx={{ width: '100%', minHeight: timeline.timeline_mode === 'aggregate_windows' ? 240 : 300, height: timeline.timeline_mode === 'aggregate_windows' ? 240 : 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 28, right: 12, left: 4, bottom: 22 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              type="number"
              dataKey="x"
              domain={domain}
              ticks={axisTicks.length >= 2 ? axisTicks : undefined}
              tickFormatter={(value) => formatDateTimeFromMillis(Number(value))}
              minTickGap={36}
              fontSize={10}
              angle={-22}
              textAnchor="end"
              height={44}
            />
            <YAxis
              tickFormatter={(value) => String(value)}
              fontSize={10}
              label={{
                value: timeline.y_axis_label,
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 11, fill: '#64748b' },
              }}
            />
            <Tooltip
              formatter={(value: number, _name, payload) => {
                const point = payload?.payload;
                if (!point) return [value, timeline.y_axis_label];
                return [
                  useRate ? `${value}/hr` : `${value} events`,
                  point.label,
                ];
              }}
              labelFormatter={(value) => formatDateTimeFromMillis(Number(value))}
            />
            {baselineStart !== null && baselineEnd !== null ? (
              <ReferenceArea
                x1={baselineStart}
                x2={baselineEnd}
                fill={TONES.baseline.fill}
                stroke={TONES.baseline.stroke}
                strokeOpacity={0.5}
                ifOverflow="extendDomain"
              />
            ) : null}
            {followupStart !== null && followupEnd !== null ? (
              <ReferenceArea
                x1={followupStart}
                x2={followupEnd}
                fill={followupTone.fill}
                stroke={followupTone.stroke}
                strokeOpacity={0.5}
                ifOverflow="extendDomain"
              />
            ) : null}
            {pivotMs !== null ? (
              <ReferenceLine
                x={pivotMs}
                stroke={TONES.pivot.stroke}
                strokeDasharray="4 4"
                label={{
                  value: timeline.corrective_action.label,
                  position: 'insideTopRight',
                  fill: TONES.pivot.label,
                  fontSize: 11,
                }}
              />
            ) : null}
            <Bar dataKey="displayValue" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={timeline.timeline_mode === 'aggregate_windows' ? 72 : 28} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
        <Chip size="small" label="Baseline window" sx={{ bgcolor: TONES.baseline.fill, color: TONES.baseline.label, border: `1px solid ${TONES.baseline.stroke}` }} />
        <Chip
          size="small"
          label="Follow-up window"
          data-testid="followup-window-chip"
          sx={{
            bgcolor: followupTone.fill,
            color: followupTone.label,
            border: `1px solid ${followupTone.stroke}`,
          }}
          data-followup-tone={timeline.followup_tone}
        />
        {pivotMs !== null ? (
          <Chip
            size="small"
            label={timeline.corrective_action.label}
            sx={{ bgcolor: '#eff6ff', color: TONES.pivot.label, border: `1px solid ${TONES.pivot.stroke}` }}
          />
        ) : null}
      </Stack>
      <ShortWindowMarker show={timeline.baseline_window.short_window} />
      <ShortWindowMarker show={timeline.followup_window.short_window} />
    </Box>
  );
}

export function CorrectiveActionImpactTimelineSection({
  timeline,
  outcomeContext,
  variant = 'full',
}: {
  timeline: CorrectiveActionImpactTimeline;
  outcomeContext?: SafetyReportOutcomeContext | null;
  variant?: 'full' | 'supplement';
}) {
  const showActivityChart =
    timeline.timeline_mode === 'event_buckets'
    && timeline.event_buckets.length >= timeline.thresholds.min_bucket_count_for_trend;
  const showImpactChart = variant === 'full'
    ? timeline.timeline_mode !== 'empty'
    : showActivityChart;
  const showChart = showImpactChart;
  const isDemoSource = Boolean(outcomeContext?.isDemoOrLoopedSource);
  const statusLabel = isDemoSource
    ? outcomeContext?.timelineStatusLabel || 'Workflow demo only'
    : timeline.status_label;
  const interpretationNote = isDemoSource
    ? outcomeContext?.timelineInterpretationNote || timeline.interpretation_note
    : timeline.interpretation_note;
  const demoIllustrationLabel = isDemoSource ? outcomeContext?.timelineIllustrationLabel : null;
  const partialStateMessages = timeline.partial_state_messages.filter((message) => {
    const normalized = String(message || '').trim().toLowerCase();
    if (!normalized) return false;
    if (demoIllustrationLabel && normalized === String(demoIllustrationLabel).trim().toLowerCase()) {
      return false;
    }
    return true;
  });

  return (
    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
      <CardContent>
        {variant === 'full' ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                Measurement windows
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isDemoSource
                  ? 'Technical illustration of baseline and follow-up windows on looped demo footage. Rates are not used to assess corrective-action effectiveness.'
                  : timeline.uses_synthetic_midpoint
                  ? 'How baseline and follow-up windows were carved from the signed-off scope using a 50% midpoint split.'
                  : 'How baseline and follow-up windows relate to the corrective action and observed activity.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={statusLabel}
                color={isDemoSource ? 'info' : badgeTone(timeline.status)}
                size="small"
                data-testid="impact-timeline-status-badge"
              />
              <Chip icon={<LockOutlined />} label="Read-only" size="small" variant="outlined" />
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                Comparison activity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Event-time trend and comparison readiness for the signed-off baseline and follow-up windows.
              </Typography>
            </Box>
            <Chip
              label={statusLabel}
              color={isDemoSource ? 'info' : badgeTone(timeline.status)}
              size="small"
              data-testid="impact-timeline-status-badge"
            />
          </Stack>
        )}

        {partialStateMessages.map((message) => (
          <Alert key={message} severity="info" icon={<InfoOutlined />} sx={{ mb: 1.5 }}>
            {message}
          </Alert>
        ))}

        {isDemoSource && demoIllustrationLabel ? (
          <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 1.5 }} data-testid="impact-timeline-demo-illustration-label">
            {demoIllustrationLabel}
          </Alert>
        ) : null}

        {showChart ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              alignItems: 'start',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                lg: showActivityChart ? 'minmax(0, 1.5fr) minmax(220px, 0.75fr)' : 'minmax(0, 1fr)',
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {showImpactChart ? <ImpactTimelineChart timeline={timeline} /> : null}
              {variant === 'full' ? <ObservationCoverageBand timeline={timeline} /> : null}
            </Box>
            <MeasurementReadinessPanel timeline={timeline} />
          </Box>
        ) : variant === 'supplement' ? (
          <MeasurementReadinessPanel timeline={timeline} />
        ) : null}

        <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2 }} data-testid="impact-timeline-interpretation-note">
          {interpretationNote}
        </Alert>
      </CardContent>
    </Card>
  );
}
